import React, { useState } from "react";
import "./styles/AmendStudent.css";
import { apiService } from "./services/apiService"; // Integrated centralized API service layer

function AmendStudent({ onSuccess }) {
  const [searchValue, setSearchValue] = useState("");
  const [admission, setAdmission] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [searched, setSearched] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    primaryContact: "",
    parentContact: "",
    registrationNo: "",
    collegeName: "",
    qualification: "",
    preparingFor: "",
    gender: "",
    dateOfBirth: "",
    email: "",
    permanentAddress: "",
    seatId: "",
    startDate: "",
    endDate: "",
    feesPaid: ""
  });

  // ✅ State to manage our glossy custom popup
  const [popup, setPopup] = useState({
    show: false,
    type: "success", // "success" | "warning" | "error"
    message: "",
    onConfirm: null // Used for the "Confirm Update" action
  });

  // Helper to show popup
  const showAlert = (type, message, onConfirm = null) => {
    setPopup({ show: true, type, message, onConfirm });
  };

  // Helper to close popup
  const closePopup = () => {
    setPopup({ show: false, type: "success", message: "", onConfirm: null });
  };

  // ── SEARCH ──
  const search = async () => {
    setAdmission(null);
    setIsDirty(false);
    setSearched(false);
    
    if (!searchValue.trim()) {
      showAlert("warning", "Please enter a search term first.");
      return;
    }

    try {
      const data = await apiService.searchAdmissions(searchValue);
      
      if (!data || data.length === 0) { 
        showAlert("error", "No student found. Try a different search.");
        setSearched(true); 
        return; 
      }
      
      const first = data[0];
      if (!first || !first.student) { 
        showAlert("error", "Invalid data payload structure returned from server.");
        return; 
      }
      
      setAdmission(first);
      setSearched(true);
      setForm({
        fullName: first.student.fullName || "",
        primaryContact: first.student.primaryContact || "",
        parentContact: first.student.parentContact || "",
        registrationNo: first.student.registrationNo || "",
        collegeName: first.student.collegeName || "",
        qualification: first.student.qualification || "",
        preparingFor: first.student.preparingFor || "",
        gender: first.student.gender || "",
        dateOfBirth: first.student.dateOfBirth || "",
        email: first.student.email || "",
        permanentAddress: first.student.permanentAddress || "",
        seatId: first.seat?.seatCode || "",
        startDate: first.startDate || "",
        endDate: first.endDate || "",
        feesPaid: first.feesPaid || ""
      });
    } catch (err) {
      console.error(err);
      showAlert("error", "Search operation failed. Please check network.");
      setSearched(true);
    }
  };

  // ── GENERAL HANDLE CHANGE ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  // ── LIVE PHONE DIGIT FILTER ──
  // Instantly blocks alphabets, symbols, spaces, and formatting characters
  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, ""); // Strip out anything that is NOT a number digit
    setForm((prev) => ({ ...prev, [name]: numericValue }));
    setIsDirty(true);
  };

  // ── VALIDATE FORM ──
  const validateForm = () => {
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.fullName.trim()) return "Full Name is required";
    
    // Primary Contact Validation
    if (!form.primaryContact.trim()) return "Primary Contact is required";
    if (!phoneRegex.test(form.primaryContact.trim())) {
      return "Primary Contact must be exactly a 10-digit mobile number";
    }

    // Optional Parent Contact Validation
    if (form.parentContact.trim() && !phoneRegex.test(form.parentContact.trim())) {
      return "Parent Contact must be exactly a 10-digit mobile number";
    }

    // Optional Email Validation
    if (form.email.trim() && !emailRegex.test(form.email.trim())) {
      return "Please enter a valid email address (e.g., name@domain.com)";
    }

    if (!form.startDate) return "Admission Start Date is required";
    if (!form.endDate) return "Expiry Date is required";
    
    // Fees Paid Validation
    if (form.feesPaid === null || form.feesPaid === undefined || String(form.feesPaid).trim() === "") {
      return "Fees Paid is required";
    }
    if (Number(form.feesPaid) < 0) return "Fees cannot be a negative value";

    if (new Date(form.endDate) <= new Date(form.startDate)) {
      return "Expiry date must be after the admission start date";
    }
      
    return null;
  };

  // ── UPDATE FLOW ──
  const triggerUpdate = () => {
    if (!admission || !admission.student) {
      showAlert("error", "No active student record selected to update.");
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      showAlert("warning", validationError);
      return;
    }

    // Ask for user confirmation utilizing our glossy modal interface instead of simple window.confirm
    showAlert(
      "warning", 
      "Are you sure you want to update this student's records?", 
      executeAdmissionUpdate
    );
  };

  const executeAdmissionUpdate = async () => {
    closePopup(); // Clear the confirmation box
    try {
      // 1. Update general student info parameters via API Layer
      await apiService.updateStudentProfile(admission.student.id, {
        fullName: form.fullName,
        primaryContact: form.primaryContact,
        parentContact: form.parentContact,
        email: form.email,
        collegeName: form.collegeName,
        qualification: form.qualification,
        preparingFor: form.preparingFor,
        permanentAddress: form.permanentAddress,
        dateOfBirth: form.dateOfBirth,
        gender: form.gender
      });

      // 2. Update structural fee/timeline markers via API Layer
      await apiService.amendAdmissionDetails(admission.id, {
        startDate: form.startDate,
        endDate: form.endDate,
        feesPaid: Number(form.feesPaid)
      });

      showAlert("success", "Student updated successfully!");
      setIsDirty(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error(err);
      showAlert("error", "Error updating student: " + (err.message || "Network issue"));
    }
  };

  // ── RESET ──
  const resetForm = () => {
    if (!admission) return;
    const s = admission.student;
    setForm({
      fullName: s.fullName || "",
      primaryContact: s.primaryContact || "",
      parentContact: s.parentContact || "",
      registrationNo: s.registrationNo || "",
      collegeName: s.collegeName || "",
      qualification: s.qualification || "",
      preparingFor: s.preparingFor || "",
      gender: s.gender || "",
      dateOfBirth: s.dateOfBirth || "",
      email: s.email || "",
      permanentAddress: s.permanentAddress || "",
      seatId: admission.seat?.seatCode || "",
      startDate: admission.startDate || "",
      endDate: admission.endDate || "",
      feesPaid: admission.feesPaid || ""
    });
    setIsDirty(false);
  };

  return (
    <div className="ams-page">

      {/* ── Header card ── */}
      <div className="ams-header-card">
        <div className="ams-header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div>
          <h2 className="ams-title">Amend Student</h2>
          <p className="ams-subtitle">Search a student to edit their details</p>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="ams-search-row">
        <input
          className="ams-search-input"
          type="text"
          placeholder="Search by Name / Mobile / Seat / Registration No"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <button className="ams-search-btn" onClick={search}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Search
        </button>
      </div>

      {/* ── No results ── */}
      {searched && !admission && (
        <div className="ams-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>No student found. Try a different search.</p>
        </div>
      )}

      {/* ── Form card ── */}
      {admission && (
        <div className="ams-card">

          {/* Student header */}
          <div className="ams-card-header">
            <div className="ams-avatar">
              {admission.student?.fullName ? admission.student.fullName.charAt(0).toUpperCase() : "?"}
            </div>
            <div>
              <div className="ams-student-name">{admission.student?.fullName}</div>
              <div className="ams-student-sub">{admission.student?.primaryContact}</div>
            </div>
            <div className="ams-seat-badge">{form.seatId}</div>
          </div>

          <div className="ams-divider" />

          {/* ── Personal details section ── */}
          <div className="ams-section-label">
            <span className="ams-section-dot" />
            Personal Details
          </div>

          <div className="ams-grid">
            <div className="ams-field">
              <label className="ams-label">Registration No</label>
              <input className="ams-input" value={form.registrationNo} disabled />
            </div>
            <div className="ams-field">
              <label className="ams-label">Seat No</label>
              <input className="ams-input" value={form.seatId} disabled />
            </div>
            <div className="ams-field">
              <label className="ams-label">Full Name <span className="ams-required">*</span></label>
              <input className="ams-input" name="fullName" value={form.fullName} onChange={handleChange} />
            </div>
            <div className="ams-field">
              <label className="ams-label">Primary Contact <span className="ams-required">*</span></label>
              <input 
                className="ams-input" 
                name="primaryContact" 
                type="tel"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={form.primaryContact} 
                onChange={handlePhoneChange} // Only numbers allowed
              />
            </div>
            <div className="ams-field">
              <label className="ams-label">Parent Contact</label>
              <input 
                className="ams-input" 
                name="parentContact" 
                type="tel"
                maxLength={10}
                placeholder="Parent's mobile number"
                value={form.parentContact} 
                onChange={handlePhoneChange} // Only numbers allowed
              />
            </div>
            <div className="ams-field">
              <label className="ams-label">Email</label>
              <input 
                className="ams-input" 
                name="email" 
                type="email"
                placeholder="student@domain.com"
                value={form.email} 
                onChange={handleChange} 
              />
            </div>
            <div className="ams-field">
              <label className="ams-label">Date of Birth</label>
              <input className="ams-input" type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} />
            </div>
            <div className="ams-field">
              <label className="ams-label">Gender</label>
              <select className="ams-input" name="gender" value={form.gender} onChange={handleChange}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          <div className="ams-divider" />

          {/* ── Academic details section ── */}
          <div className="ams-section-label">
            <span className="ams-section-dot" />
            Academic Details
          </div>

          <div className="ams-grid">
            <div className="ams-field">
              <label className="ams-label">College Name</label>
              <input className="ams-input" name="collegeName" value={form.collegeName} onChange={handleChange} />
            </div>
            <div className="ams-field">
              <label className="ams-label">Qualification</label>
              <input className="ams-input" name="qualification" value={form.qualification} onChange={handleChange} />
            </div>
            <div className="ams-field">
              <label className="ams-label">Preparing For</label>
              <input className="ams-input" name="preparingFor" value={form.preparingFor} onChange={handleChange} />
            </div>
            <div className="ams-field ams-field-full">
              <label className="ams-label">Permanent Address</label>
              <input className="ams-input" name="permanentAddress" value={form.permanentAddress} onChange={handleChange} />
            </div>
          </div>

          <div className="ams-divider" />

          {/* ── Admission details section ── */}
          <div className="ams-section-label">
            <span className="ams-section-dot" />
            Admission Details
          </div>

          <div className="ams-grid">
            <div className="ams-field">
              <label className="ams-label">Admission Date</label>
              <input className="ams-input" type="date" name="startDate" value={form.startDate} onChange={handleChange} />
            </div>
            <div className="ams-field">
              <label className="ams-label">Expiry Date</label>
              <input className="ams-input" type="date" name="endDate" value={form.endDate} onChange={handleChange} />
            </div>
            <div className="ams-field">
              <label className="ams-label">Fees Paid (₹)</label>
              <input 
                className="ams-input" 
                type="number" 
                name="feesPaid" 
                min="0"
                placeholder="0"
                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                value={form.feesPaid} 
                onChange={handleChange} 
              />
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="ams-actions">
            <button className="ams-btn ams-btn-reset" onClick={resetForm}>
              Reset
            </button>
            <button
              className={`ams-btn ams-btn-update ${!isDirty ? "ams-btn-disabled" : ""}`}
              disabled={!isDirty}
              onClick={triggerUpdate}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Update Student
            </button>
          </div>

        </div>
      )}

      {/* ✅ GLOSSY POPUP MODAL (Shares styling setup with na-popup styles) */}
      {popup.show && (
        <div className="na-popup-overlay" onClick={closePopup}>
          <div 
            className={`na-popup-card na-popup-${popup.type}`} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="na-popup-glow"></div>
            
            <div className="na-popup-icon-container">
              {popup.type === "success" && (
                <div className="na-popup-icon na-pop-success">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
              )}
              {popup.type === "warning" && (
                <div className="na-popup-icon na-pop-warning">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
              )}
              {popup.type === "error" && (
                <div className="na-popup-icon na-pop-error">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}
            </div>

            <h3 className="na-popup-title">
              {popup.type === "success" && "Success!"}
              {popup.type === "warning" && "Confirm Action"}
              {popup.type === "error" && "Error"}
            </h3>
            
            <p className="na-popup-message">{popup.message}</p>
            
            <div style={{ display: "flex", gap: "12px", width: "100%", justifyContent: "center", zIndex: 2 }}>
              {popup.onConfirm ? (
                <>
                  <button 
                    className="na-popup-btn" 
                    style={{ background: "#64748b" }} 
                    onClick={closePopup}
                  >
                    Cancel
                  </button>
                  <button 
                    className="na-popup-btn" 
                    onClick={popup.onConfirm}
                  >
                    Yes, Update
                  </button>
                </>
              ) : (
                <button className="na-popup-btn" onClick={closePopup}>
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AmendStudent;